import { useQuery } from '@based/sdk/react'
import { Blog } from '@schema/blog'
import { Stories } from '@schema/stories'
import { News } from '@schema/news'
import { Title } from '@app/components/core/typography/Title'
import { Text } from '@app/components/core/typography/Text'
import { SafeHtml } from '@app/components/core/safeHtml/SafeHtml'
import Column from '@app/components/layout/Column'
import { styled } from 'inlines'
import { Author } from '@app/components/blocks/author/Author'
import useMediaQuery from '@app/util/useMediaQuery'
import { pages } from '@app/util/links'
import { useRoute } from 'wouter'
import { Image } from '@app/components/core/image/Image'
import { expandImage } from '@app/types/StoredImage'
import { useTranslations } from '@i18n/useLanguage'
import useDateFormat from '@app/util/useDateFormat'
import { PageSeo } from '@app/components/core/seo/PageSeo'
import { buildArticleJsonLd } from '@app/util/seo/buildArticleJsonLd'
import { useRootDomain } from '@app/util/hooks/useRootDomain'
import { getEntityPageStatus } from '@app/util/getEntityPageStatus'
import { NotFoundPage } from '@app/pages/error/NotFoundPage'
import { PageLayout } from '@app/pages/layout/PageLayout'
import { RegionalPageLayout } from '@app/pages/layout/RegionalPageLayout'
import { RegionalBreadcrumbs } from '@app/components/blocks/pageBreadcrumbs/RegionalBreadcrumbs'
import { RegionViewModel } from 'functions/app/geography/view/region'
import { match } from 'ts-pattern'
import { regionsTabs } from '@app/pages/discover/regions/constants'

export type RegionalBlogItemPageProps = {
  regionSlug: string
  blogId: string
}

const sharedParagraphStyles = {
  maxWidth: '50rem',
  marginLeft: 'auto',
  marginRight: 'auto',
  width: '100%',
}

const blogTypeToQueryMapping = {
  verhalen: 'view-stories',
  news: 'view-news',
  blog: 'view-blog',
} as const

export const RegionalBlogItemPage = ({
  regionSlug,
  blogId,
}: RegionalBlogItemPageProps) => {
  const [, params] = useRoute<{ blogType: string }>(
    pages.regionArticle(':slug', ':blogType', ':blogId'),
  )
  const blogType =
    params?.blogType && params.blogType in blogTypeToQueryMapping
      ? (params.blogType as keyof typeof blogTypeToQueryMapping)
      : null
  const isMobile = useMediaQuery()
  const t = useTranslations()
  const dateFormat = useDateFormat()
  const queryName = blogType ? blogTypeToQueryMapping[blogType] : null
  const itemQuery = useQuery(queryName, blogType ? { id: blogId } : undefined)
  const regionQuery = useQuery('view-region', { slug: regionSlug })
  const item: Blog | Stories | News | undefined = itemQuery.data
  const region: RegionViewModel | undefined = regionQuery.data
  const rootDomain = useRootDomain()

  const pageStatus = getEntityPageStatus(itemQuery.loading, item)
  const regionPageStatus = getEntityPageStatus(regionQuery.loading, region)

  if (pageStatus === 'not-found' || regionPageStatus === 'not-found') {
    return <NotFoundPage />
  }

  if (
    pageStatus === 'loading' ||
    regionPageStatus === 'loading' ||
    !item ||
    !region
  ) {
    return (
      <PageLayout gap="2rem">
        <PageSeo />
        {t.app.blogPage.loading}
      </PageLayout>
    )
  }

  const { src, altText } = expandImage(item.images?.[0], item.title)
  const actueelLabel = match(blogType)
    .with('verhalen', () => t.app.blogPage.tabs.stories)
    .with('news', () => t.app.blogPage.tabs.news)
    .with('blog', () => t.app.blogPage.tabs.blog)
    .exhaustive()
  const articleUrl = `${rootDomain}${pages.regionArticle(regionSlug, blogType, blogId)}`
  const actueelPath = pages.regionPageNew(regionSlug, regionsTabs.current)

  return (
    <RegionalPageLayout region={region} gap="0rem" tab={regionsTabs.current}>
      <PageSeo
        pageTitle={item.title}
        description={item.intro}
        meta={{
          metaTitle: item.metaTitle,
          metaDescription: item.metaDescription,
        }}
        image={item.images?.[0]}
        type="article"
        jsonLd={buildArticleJsonLd({
          headline: item.title,
          description: item.intro,
          url: articleUrl,
          imageUrl: src,
          datePublished: new Date(item.publishedAt).toISOString(),
          authorName: `${item.author.firstName} ${item.author.lastName}`,
        })}
      />

      <RegionalBreadcrumbs
        regionName={region.name}
        regionHomePath={pages.regionPageNew(regionSlug)}
        sectionLabel={actueelLabel}
        sectionPath={actueelPath}
        articleTitle={item.title}
      />

      <Column
        style={{
          ...sharedParagraphStyles,
          marginBottom: isMobile ? '2rem' : '4rem',
          marginTop: isMobile ? '1rem' : '2rem',
        }}
      >
        <div>
          <Text.Small style={{ fontWeight: 500, marginBottom: '0.25rem' }}>
            {dateFormat(new Date(item.publishedAt))}
          </Text.Small>
          <Title.Page styledAsLevel={isMobile ? 2 : 1} style={{ margin: 0 }}>
            {item.title}
          </Title.Page>
        </div>
        <Text.Large>{item.intro}</Text.Large>
        <Author name={`${item.author.firstName} ${item.author.lastName}`} />
      </Column>

      {src && (
        <Image
          src={src}
          alt={altText}
          style={{
            borderRadius: '1.25rem',
            marginBottom: '2rem',
            maxHeight: isMobile ? '12rem' : '35rem',
            width: '100%',
            objectFit: 'cover',
          }}
        />
      )}

      <styled.div style={sharedParagraphStyles}>
        <SafeHtml html={item.content} />
      </styled.div>
    </RegionalPageLayout>
  )
}
